import React, { useState } from 'react';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import AiPill from '../components/AiPill';

// My No / SCAM — Feedback. The five questions come from the curated QA doc:
// three 1-5 ratings, a yes/no recommendation, and 256 characters of free text.
const RATING_QUESTIONS = [
  { number: 1, key: 'awarenessBefore', label: 'Rate your ScammerAwareness before using No / SCAM' },
  { number: 2, key: 'awarenessAfter', label: 'Rate your ScammerAwareness using No / SCAM' },
  { number: 4, key: 'effectiveness', label: 'Rate No / SCAM effectiveness in helping you avoid being victimized by scammers' },
];

const RATING_SCALE = [1, 2, 3, 4, 5];
const FUTURE_MAX = 256;

const QuestionRow = ({ number, label, children }) => (
  <div className="bg-[#A8CBDD] px-5 py-4 mb-4">
    <p className="font-bold text-black mb-3">
      Q{number} &raquo; {label}?
    </p>
    {children}
  </div>
);

const FeedbackPage = () => {
  const [answers, setAnswers] = useState({
    awarenessBefore: null,
    awarenessAfter: null,
    wouldRecommend: null,
    effectiveness: null,
    futureRelease: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  // Every question is optional, but an entirely empty survey isn't worth storing.
  const hasAnyAnswer =
    RATING_QUESTIONS.some((q) => answers[q.key] !== null) ||
    answers.wouldRecommend !== null ||
    answers.futureRelease.trim() !== '';

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAnyAnswer) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...answers,
          futureRelease: answers.futureRelease.trim() || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.errors?.[0]?.msg || d.error || 'Failed to submit');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <HubPageLayout>
        <div className="border-2 border-black mt-10 p-6 min-h-[380px]">
          <AiPill>Working towards common purpose.</AiPill>
          <p className="text-3xl text-black mt-10 px-2 leading-snug">
            We appreciate you taking the time to help us innovate.
          </p>
        </div>
      </HubPageLayout>
    );
  }

  const ratingButton = (key, value) => {
    const selected = answers[key] === value;
    return (
      <button
        key={value}
        type="button"
        onClick={() => setAnswer(key, selected ? null : value)}
        aria-pressed={selected}
        className={`w-11 h-11 rounded-full border-2 transition-colors ${
          selected
            ? 'bg-black text-white border-black font-semibold'
            : 'bg-white text-black border-gray-500 hover:border-black'
        }`}
      >
        {value}
      </button>
    );
  };

  return (
    <HubPageLayout>
      <FramedPanel title="Feedback">
        <div className="px-6 pt-8 pb-6">
          <img
            src="/images/my-noscam/feedback-hero.jpeg"
            alt=""
            className="w-full h-[280px] object-cover"
          />
        </div>
      </FramedPanel>

      <p className="text-lg text-black mt-6 mb-6">
        Help us make No / SCAM better — let&rsquo;s turn your ideas into innovation targeting online
        scammers.
      </p>

      <form onSubmit={submit}>
        {/* Q1 and Q2 — awareness before / after */}
        {RATING_QUESTIONS.filter((q) => q.number < 3).map((q) => (
          <QuestionRow key={q.key} number={q.number} label={q.label}>
            <div className="flex items-center gap-3 flex-wrap">
              {RATING_SCALE.map((v) => ratingButton(q.key, v))}
              <span className="text-sm text-black/70 ml-2">1 = low, 5 = high</span>
            </div>
          </QuestionRow>
        ))}

        <QuestionRow number={3} label="Would you recommend No / SCAM to a family member or friend">
          <div className="flex items-center gap-3">
            {[['Yes', true], ['No', false]].map(([label, value]) => {
              const selected = answers.wouldRecommend === value;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setAnswer('wouldRecommend', selected ? null : value)}
                  aria-pressed={selected}
                  className={`px-6 py-2 rounded-full border-2 transition-colors ${
                    selected
                      ? 'bg-black text-white border-black font-semibold'
                      : 'bg-white text-black border-gray-500 hover:border-black'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </QuestionRow>

        {/* Q4 — effectiveness */}
        {RATING_QUESTIONS.filter((q) => q.number === 4).map((q) => (
          <QuestionRow key={q.key} number={q.number} label={q.label}>
            <div className="flex items-center gap-3 flex-wrap">
              {RATING_SCALE.map((v) => ratingButton(q.key, v))}
              <span className="text-sm text-black/70 ml-2">1 = low, 5 = high</span>
            </div>
          </QuestionRow>
        ))}

        <QuestionRow number={5} label="Tell us what you&rsquo;d like to see in a future No / SCAM release">
          <textarea
            value={answers.futureRelease}
            onChange={(e) => setAnswer('futureRelease', e.target.value.slice(0, FUTURE_MAX))}
            rows={4}
            maxLength={FUTURE_MAX}
            className="w-full border border-gray-500 p-3 text-black focus:outline-none focus:border-black"
          />
          <p className="text-sm text-black/70 mt-1">
            {answers.futureRelease.length} / {FUTURE_MAX}
          </p>
        </QuestionRow>

        <div className="flex items-center gap-4 mt-6">
          <button
            type="submit"
            disabled={submitting || !hasAnyAnswer}
            className="bg-black text-white text-lg px-10 py-3 rounded-lg border-2 border-gray-700 hover:bg-gray-900 disabled:opacity-50"
          >
            {submitting ? 'Sending…' : 'Submit feedback'}
          </button>
          {error && <p className="text-red-600">{error}</p>}
        </div>
      </form>
    </HubPageLayout>
  );
};

export default FeedbackPage;
